/**
 * Play with friends: create a private table or join one by code / invite link, then wait in the
 * lobby (src/api/table-room.ts runs the table). Only shown when the server says multiplayer is open.
 *
 * The browser only ever asks: every change goes to the table, which answers with the new table for
 * everyone. If the connection drops it reconnects by itself (1 s, 2 s, 4 s … up to 15 s) and the
 * table sends the latest state; a refresh rejoins the same seat.
 */
import type { ClientMessage, PublicTable, ServerMessage } from "../shared/table-protocol.ts";
import { PALETTE, distinct, type NamedColour } from "./colours.ts";
import { colourWheel, type ColourWheel } from "./colour-wheel.ts";
import { personaPicById, pickOpponents } from "./opponents.ts";
import { PERSONAS } from "../engine/personas.ts";
import { aiCount, CODE_LENGTH, EXTEND_DAYS, GAME_DAYS, isCode, LOBBY_DAYS, MAX_AI, REMIND_DAYS, SEATS } from "../shared/table-protocol.ts";

const $ = (id: string) => document.getElementById(id)!;
const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
const inkOn = (hex: string) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return (r * 299 + g * 587 + b * 114) / 1000 > 165 ? "#111" : "#fff";   // white on mid tones, black only on light ones
};
/** "in 6 days", "in 1 day", "in 5 hours" — and the date, for when a table closes by itself. */
function closesIn(at: number): { text: string; soon: boolean } {
  const ms = at - Date.now(), day = 86_400_000;
  const left = ms >= 2 * day ? `${Math.round(ms / day)} days` : ms >= day ? "1 day" : `${Math.max(1, Math.ceil(ms / 3_600_000))} hour${ms > 3_600_000 ? "s" : ""}`;
  const date = new Date(at).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  return { text: `in ${left} (${date})`, soon: ms <= REMIND_DAYS * day };
}

/** Close codes that mean "don't reconnect by yourself". */
const FINAL = new Set([4001, 4002, 4003, 4004, 4005, 4006, 4008, 4010]);

interface Host {
  onClose(): void;
  needSignIn(): boolean;
  /** A game message for this seat (the game screen plays it). */
  onGame(m: Extract<ServerMessage, { type: "game" }>): void;
  /** One of your game moves was refused (out of date, or not allowed). */
  onMoveRefused(error: string): void;
  /** The table you're playing at closed (the host closed it). */
  onGameGone(reason: string): void;
}
/** Ids of game moves sent and not yet answered (so a refusal goes back to the game screen). */
const pendingMoves = new Set<string>();
/** What each recent message said, so one refused only for coming too fast can be sent again. */
const sentBody = new Map<string, string>();

let host: Host;
let tableId: string | null = null;
let ws: WebSocket | null = null;
let table: PublicTable | null = null;
let you: Extract<ServerMessage, { type: "table" }>["you"] | null = null;
let status: "connecting" | "connected" | "reconnecting" | "closed" = "closed";
let closedReason = "";
let retry = 0, retryTimer = 0;
let note = "";
/** The game screen has taken over (the game started). */
let gameOn = false;
/** The table whose "Close table" is waiting for "Yes, close it". */
let confirmClose: string | null = null;

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(path, { ...init, headers: { "content-type": "application/json", ...(init.headers ?? {}) }, credentials: "same-origin" });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error((j as { message?: string }).message ?? `Something went wrong (${r.status}).`);
  return j as T;
}

/** Is multiplayer open on this server? */
export async function multiplayerOpen(): Promise<boolean> {
  try { return (await api<{ multiplayer: boolean }>("/api/config")).multiplayer; } catch { return false; }
}

export function initTables(h: Host): void {
  host = h;
  $("mp-back").onclick = closeScreen;
  $("mp-create").onclick = () => void create();
  const input = $("mp-code") as HTMLInputElement;
  input.maxLength = CODE_LENGTH;
  input.addEventListener("input", () => { input.value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, ""); });
  $("mp-join").onclick = () => void joinByCode(input.value);
  input.addEventListener("keydown", (e) => { if (e.key === "Enter") void joinByCode(input.value); });
  $("mp-lobby").addEventListener("click", onLobbyClick);
  $("mp-mine").addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const id = t.closest<HTMLElement>("[data-rejoin]")?.dataset.rejoin;
    if (id) { void connect(id); return; }
    const close = t.closest<HTMLElement>("[data-close]")?.dataset.close;
    if (close) { confirmClose = close; drawMine(); return; }
    const yes = t.closest<HTMLElement>("[data-close-yes]")?.dataset.closeYes;
    if (yes) { void closeTable(yes); return; }
    if (t.closest("[data-close-no]")) { confirmClose = null; drawMine(); return; }
    const keep = t.closest<HTMLElement>("[data-keep]")?.dataset.keep;
    if (keep) void keepLonger(keep);
  });
}

/** Open the Play with friends screen (optionally straight into a room code from an invite link). */
export function openTables(code?: string): void {
  if (host.needSignIn()) return;
  $("mp").classList.remove("hidden");
  if (code) { void joinByCode(code); return; }
  if (tableId && ws) render();
  else showStart();
}

/** Leave this screen's connection (your seat stays yours) and go back to the list of tables. */
function backToTables(): void {
  disconnect();
  tableId = null; table = null; you = null; status = "closed"; closedReason = ""; note = "";
  showStart();
}

function closeScreen(): void {
  disconnect();
  tableId = null; table = null; you = null; status = "closed"; closedReason = ""; note = "";
  $("mp").classList.add("hidden");
  host.onClose();
}

function showStart(msg = ""): void {
  $("mp-start").hidden = false; $("mp-lobby").hidden = true;
  $("mp-start-error").textContent = msg;
  void listMine();
}

interface MyTable { id: string; code: string; status: string; host: string; isHost: boolean; players: string; closesAt: number | null }
let myTables: MyTable[] = [];

/** Your open tables (waiting in the lobby, or a game in play), each with Rejoin. */
async function listMine(): Promise<void> {
  try { myTables = (await api<{ tables: MyTable[] }>("/api/tables/mine")).tables; drawMine(); }
  catch { myTables = []; drawMine(); }
}
function drawMine(): void {
  const box = $("mp-mine"), tables = myTables;
  box.hidden = false;
  box.innerHTML = `<h2 class="ui-h2">Your tables</h2>${tables.length ? `<ul class="mp-mine">${tables.map((t) => `<li>
      <span><b class="mp-code">${esc(t.code)}</b> <span class="mp-tag${t.status === "playing" ? " gold" : ""}">${t.status === "playing" ? "Game in play" : "In the lobby"}</span>
      <span class="mp-mute mp-small-text">Host ${esc(t.host)}${t.players ? ` · ${esc(t.players)}` : ""}</span>
      ${t.closesAt ? closesLine(t.closesAt, "Closes", " unless someone comes back",
        t.isHost ? `<button class="ui-link" type="button" data-keep="${esc(t.id)}">Keep ${EXTEND_DAYS} more days</button>` : "") : ""}</span>
      ${confirmClose === t.id ? closeAsk(t.id, t.status === "playing")
        : `<span class="mp-row-btns">${t.isHost ? `<button class="ui-btn-ghost" type="button" data-close="${esc(t.id)}">Close table</button>` : ""}
        <button class="ui-btn" type="button" data-rejoin="${esc(t.id)}">${t.status === "playing" ? "Rejoin game" : "Open"}</button></span>`}</li>`).join("")}</ul>`
    : `<p class="mp-mute mp-none">No open tables. Tables you create or join are listed here until they finish or close.</p>`}
    <p class="mp-mute mp-small-text mp-life">A table closes by itself after ${LOBBY_DAYS} days with nobody in the lobby, or ${GAME_DAYS} days with nobody at a game in play. The host is emailed when ${REMIND_DAYS} days are left, and can keep it ${EXTEND_DAYS} more days at a time, or close it any time.</p>`;
}

/** "Closes in 6 days (Sat 7 Nov)" — red when it's close — with the host's Keep 5 more days. */
function closesLine(at: number, lead: string, tail: string, keep: string): string {
  const c = closesIn(at);
  return `<span class="mp-closes${c.soon ? " soon" : ""}"><span>${lead} ${esc(c.text)}${tail}</span>${keep}</span>`;
}

/** The host keeps a table EXTEND_DAYS longer. */
async function keepLonger(id: string): Promise<void> {
  try {
    const { closesAt } = await api<{ closesAt: number }>(`/api/tables/${id}/extend`, { method: "POST", body: "{}" });
    const m = myTables.find((t) => t.id === id);
    if (m) { m.closesAt = closesAt; drawMine(); }
    if (tableId === id && table) { table.closesAt = closesAt; note = `Kept — it now closes ${closesIn(closesAt).text} if nobody comes back.`; render(); }
  } catch (e) {
    if (tableId === id && table) { note = (e as Error).message; render(); } else showStart((e as Error).message);
  }
}

/** "Are you sure?" for closing a table, in place of its buttons. */
function closeAsk(id: string, playing: boolean): string {
  return `<span class="mp-close-ask"><span>${playing ? "End the game and close this table for everyone? The game won't be recorded." : "Close this table for everyone?"}</span>
    <span class="mp-row-btns"><button class="ui-btn-ghost" type="button" data-close-no>Cancel</button>
    <button class="ui-btn mp-danger" type="button" data-close-yes="${esc(id)}">Yes, close it</button></span></span>`;
}

/** The host closes a table for good: everyone at it is sent back to their list of tables. */
async function closeTable(id: string): Promise<void> {
  confirmClose = null;
  try {
    await api(`/api/tables/${id}`, { method: "DELETE" });
    if (tableId === id) { disconnect(); tableId = null; table = null; you = null; status = "closed"; closedReason = ""; note = ""; }
    showStart("Table closed.");
  } catch (e) {
    if (tableId === id && table) { note = (e as Error).message; render(); }
    else showStart((e as Error).message);
  }
}

async function create(): Promise<void> {
  $("mp-start-error").textContent = "";
  try { const t = await api<{ id: string; code: string }>("/api/tables", { method: "POST", body: "{}" }); await connect(t.id); }
  catch (e) { $("mp-start-error").textContent = (e as Error).message; }
}

async function joinByCode(raw: string): Promise<void> {
  const code = raw.trim().toUpperCase();
  if (!isCode(code)) { showStart(`Room codes are ${CODE_LENGTH} letters and numbers.`); return; }
  try { const t = await api<{ id: string }>(`/api/tables/code/${code}`); await connect(t.id); }
  catch (e) { showStart((e as Error).message); }
}

async function connect(id: string): Promise<void> {
  if (ws && tableId === id && status !== "closed") return;
  disconnect();
  tableId = id;
  status = retry ? "reconnecting" : "connecting";
  render();
  const sock = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/tables/${id}/ws`);
  ws = sock;
  sock.onopen = () => { if (ws !== sock) return; retry = 0; status = "connected"; render(); };
  /** The table's own "closing" notice: acted on at once, as the socket itself can take a while to finish closing. */
  let final = 0;
  sock.onmessage = (e) => {
    if (ws !== sock) return;
    const m = JSON.parse(e.data) as ServerMessage;
    onMessage(m);
    if (m.type === "closing" && FINAL.has(m.code)) { final = m.code; sock.onclose?.(new CloseEvent("close", { code: m.code, reason: m.reason })); }
  };
  sock.onclose = (e) => {
    if (ws !== sock) return;
    ws = null;
    if (final) try { sock.close(); } catch { /* */ }
    if (FINAL.has(e.code)) {
      status = "closed"; closedReason = closedReason || e.reason || "Disconnected.";
      if (e.code === 4010 && gameOn) { gameOn = false; tableId = null; table = null; host.onGameGone(closedReason); closedReason = ""; return; }
      if (e.code === 4005 || e.code === 4010 || e.code === 4003 || e.code === 4004) { tableId = null; table = null; showStart(closedReason); closedReason = ""; return; }
      render();
      return;
    }
    // dropped: try again with backoff
    status = "reconnecting"; render();
    const wait = Math.min(15000, 1000 * 2 ** retry++);
    retryTimer = window.setTimeout(() => { if (tableId) void connect(tableId); }, wait);
  };
}

function disconnect(): void {
  clearTimeout(retryTimer);
  if (ws) { const s = ws; ws = null; try { s.close(1000); } catch { /* */ } }
}

function onMessage(m: ServerMessage): void {
  if (m.type === "table") { table = m.table; you = m.you; if (m.table.status === "lobby") render(); else if (!gameOn) render(); }
  else if (m.type === "game") { if (table) table.version = Math.max(table.version, m.v); gameOn = true; host.onGame(m); }
  else if (m.type === "ack") {
    const body = sentBody.get(m.id); sentBody.delete(m.id);
    if (m.retry && body && ws) { const s = ws; setTimeout(() => { if (s.readyState === WebSocket.OPEN) { sentBody.set(m.id, body); s.send(body); } }, 1500); return; }
    const move = pendingMoves.delete(m.id);
    if (!m.ok && m.error) { if (move) host.onMoveRefused(m.error); else { note = m.error; render(); } }
  }
  else if (m.type === "closing") closedReason = m.reason;
}

function send(m: Omit<ClientMessage, "id" | "v"> & { type: ClientMessage["type"] }): string | null {
  if (!ws || ws.readyState !== WebSocket.OPEN || !table) return null;
  note = "";
  const id = crypto.randomUUID();
  const body = JSON.stringify({ ...m, id, v: table.version });
  sentBody.set(id, body);
  if (sentBody.size > 50) sentBody.delete(sentBody.keys().next().value!);
  ws.send(body);
  return id;
}

/** From the game screen: a move or "continue" for this table. */
export function sendToTable(m: { type: "move"; move: unknown } | { type: "continue"; hand: number } | { type: "peek"; hand: number }): void {
  const id = send(m as never);
  if (id && (m.type === "move" || m.type === "peek")) pendingMoves.add(id);
}

function onLobbyClick(e: Event): void {
  const t = (e.target as HTMLElement).closest<HTMLElement>("[data-do]");
  if (!t) return;
  const seat = Number(t.dataset.seat);
  switch (t.dataset.do) {
    case "ready": send({ type: "ready", ready: t.dataset.on !== "1" } as never); break;
    case "add-ai": send({ type: "addAI", seat } as never); break;
    case "choose-ai": {
      const here = (table?.seats ?? []).flatMap((x) => (x ? [x.name] : []));
      void pickOpponents({ max: 1, exclude: here, title: `Choose the AI player for seat ${seat + 1}` }).then((got) => {
        if (got === null) return;
        send({ type: "addAI", seat, ...(got[0] ? { persona: got[0] } : {}) } as never);
      });
      break;
    }
    case "remove-ai": send({ type: "removeAI", seat } as never); break;
    case "start": send({ type: "start" }); break;
    case "leave": send({ type: "leave" }); break;
    case "takeover": if (ws) send({ type: "takeover" }); else if (tableId) { closedReason = ""; void connect(tableId); } break;
    case "copy": void copyInvite(); break;
    case "tables": backToTables(); break;
    case "close": confirmClose = tableId; render(); $("mp-lobby").querySelector(".mp-close-ask")?.scrollIntoView({ block: "nearest", behavior: "smooth" }); break;
    case "close-no": confirmClose = null; render(); break;
    case "close-yes": if (tableId) void closeTable(tableId); break;
    case "keep": if (tableId) void keepLonger(tableId); break;
    case "colour": send({ type: "setColour", colour: t.dataset.colour } as never); break;
    case "retry": if (tableId) { retry = 0; closedReason = ""; void connect(tableId); } break;
  }
}

async function copyInvite(): Promise<void> {
  if (!table) return;
  const link = `${location.origin}/?table=${table.code}`;
  try { await navigator.clipboard.writeText(link); note = "Invite link copied — send it to your friends."; }
  catch { note = `Invite link: ${link}`; }
  render();
}

function render(): void {
  const lobby = $("mp-lobby");
  $("mp-start").hidden = true; lobby.hidden = false;
  if (!table || !you) {
    lobby.innerHTML = `<p class="mp-status">${status === "closed" ? esc(closedReason || "Disconnected.") : "Connecting to the table…"}</p>
      ${status === "closed" ? `<button class="ui-btn" data-do="retry" type="button">Try again</button> <button class="ui-btn-ghost" data-do="tables" type="button">‹ All my tables</button>` : ""}`;
    return;
  }
  const t = table, me = you, aiFull = aiCount(t.seats) >= MAX_AI;
  const conn = status === "connected" ? `<span class="mp-pill ok">● Connected</span>`
    : status === "closed" ? `<span class="mp-pill bad">● ${esc(closedReason || "Disconnected")}</span>`
    : `<span class="mp-pill warn">● Reconnecting…</span>`;
  const seats = t.seats.map((s, i) => {
    if (!s) return `<li class="mp-seat empty"><span class="mp-no">${i + 1}</span><span class="mp-empty">Empty seat</span>
      ${me.host && t.status === "lobby" && !aiFull ? `<span class="mp-row-btns"><button class="ui-btn-ghost mp-small" data-do="add-ai" data-seat="${i}" type="button">Random AI</button>
        <button class="ui-btn-ghost mp-small" data-do="choose-ai" data-seat="${i}" type="button">Choose AI…</button></span>`
        : `<span class="mp-mute">${aiFull ? "Kept for a person — invite a friend" : "Waiting for a player…"}</span>`}</li>`;
    const aiPic = s.kind === "ai" ? personaPicById(s.persona ?? PERSONAS.find((x) => x.name === s.name)?.id, 44) : undefined;
    const drawn = s.kind === "human" && !s.avatarUrl && s.avatarCfg ? drawAvatar(s.avatarCfg, s.colour) : "";
    const pic = aiPic ? `<span class="mp-av mp-av-ai">${aiPic}</span>` : s.kind === "human" && s.avatarUrl ? `<img class="mp-av" src="${esc(s.avatarUrl)}" alt="">`
      : drawn ? `<span class="mp-av mp-av-ai">${drawn}</span>`
      : `<span class="mp-av" style="background:${s.colour};color:${inkOn(s.colour)}">${s.kind === "ai" ? "AI" : esc(s.name.slice(0, 1).toUpperCase())}</span>`;
    const party = `<span class="mp-party" style="background:${s.colour};color:${inkOn(s.colour)}">${esc(s.party)}</span>`;
    let state = "";
    if (s.kind === "ai") state = `<span class="mp-tag"${s.style ? ` title="How ${esc(s.name)}'s AI plays, from their record"` : ""}>AI${s.style ? ` · ${esc(s.style)}` : " player"}</span>`;
    else {
      const tags = [t.hostId === s.playerId ? `<span class="mp-tag gold">Host</span>` : "",
        !s.connected ? `<span class="mp-tag bad">Disconnected</span>` : s.ready ? `<span class="mp-tag ok">✓ Ready</span>` : `<span class="mp-tag">Not ready</span>`];
      state = tags.join("");
    }
    const changed = s.kind === "human" && s.colour !== s.preferred ? `<span class="mp-mute mp-col" title="Someone seated before you has that colour">Your colour is taken here — you're this colour at this table</span>` : "";
    // your own seat in the lobby: pick your colour (colours another person here has are greyed out)
    const others = t.seats.flatMap((o, j) => (o?.kind === "human" && j !== i ? [{ c: o.colour, n: o.name }] : []));
    const picker = i === me.seat && s.kind === "human" && t.status === "lobby" ? `<span class="mp-swatches" role="group" aria-label="Your colour">${PALETTE.map((p) => {
      const taken = others.find((o) => !distinct(p.colour, o.c));
      const on = p.colour === s.colour;
      return `<button class="mp-sw${on ? " on" : ""}" type="button" data-do="colour" data-colour="${p.colour}" style="--sw:${p.colour}"
        ${taken || on || !me.controlling ? "disabled" : ""} title="${taken ? `${esc(taken.n)} has this colour` : on ? `${p.name} — your colour` : p.name}" aria-label="${p.name}${taken ? " (taken)" : on ? " (yours)" : ""}"></button>`;
    }).join("")}</span>` : "";
    const ctl = s.kind === "ai" && me.host && t.status === "lobby" ? `<button class="ui-link" data-do="remove-ai" data-seat="${i}" type="button">Remove</button>` : "";
    return `<li class="mp-seat${i === me.seat ? " mine" : ""}" style="--c:${s.colour}"><span class="mp-no">${i + 1}</span>${pic}
      <span class="mp-who"><b>${esc(s.name)}${i === me.seat ? " (you)" : ""}</b>${party}${picker}${i === me.seat && !picker ? changed : ""}</span>
      <span class="mp-state">${state}${ctl}</span></li>`;
  }).join("");
  const mine = t.seats[me.seat];
  const ready = mine?.kind === "human" && mine.ready;
  const takeover = status === "closed" ? `<div class="mp-notice">${esc(closedReason || "You're disconnected from this table.")}
      <button class="ui-btn" data-do="takeover" type="button">Reconnect on this device</button></div>`
    : !me.controlling ? `<div class="mp-notice">This table is open on another of your devices.
      <button class="ui-btn" data-do="takeover" type="button">Take over on this device</button></div>` : "";
  const started = t.status === "playing" ? `<div class="mp-notice">The game has started and the seats are locked. Playing hands together arrives in the next update.</div>` : "";
  const actions = t.status !== "lobby" ? "" : `
    <div class="mp-actions">
      <button class="${ready ? "ui-btn-ghost" : "ui-btn ui-btn-lg"}" data-do="ready" data-on="${ready ? 1 : 0}" type="button" ${me.controlling ? "" : "disabled"}>${ready ? "Not ready yet" : "I'm ready"}</button>
      ${me.host ? `<button class="ui-btn ui-btn-lg" data-do="start" type="button" ${t.blockers.length || !me.controlling ? "disabled" : ""}>Start game</button>` : ""}
    </div>
    ${t.blockers.length ? `<ul class="mp-blockers">${t.blockers.map((b) => `<li>${esc(b)}</li>`).join("")}</ul>`
      : `<p class="mp-go">${me.host ? "Everyone's ready — start when you like." : "Everyone's ready — waiting for the host to start."}</p>`}`;
  lobby.innerHTML = `
    <div class="mp-head">
      <div><p class="mp-kicker">Private table</p><h2 class="ui-h2">Room code <span class="mp-code">${esc(t.code)}</span></h2></div>
      <div class="mp-head-r">${conn}<button class="ui-btn-ghost mp-small" data-do="copy" type="button">Copy invite link</button>
        <button class="ui-btn-ghost mp-small" data-do="tables" type="button">‹ All my tables</button></div>
    </div>
    <p class="mp-mute">Friends join with the link or by entering the code under Play with friends. A game needs ${SEATS} seats filled — at least 2 people, and up to ${MAX_AI} AI players.</p>
    ${takeover}${started}
    <ol class="mp-seats">${seats}</ol>
    ${actions}
    ${note ? `<p class="mp-note" role="status">${esc(note)}</p>` : ""}
    <p class="mp-exit">${closesLine(t.closesAt, "If everyone leaves, this table closes", "",
      me.host ? `<button class="ui-link" data-do="keep" type="button">Keep ${EXTEND_DAYS} more days</button>` : "")}</p>
    ${me.host ? (confirmClose === t.id
      ? `<div class="mp-notice mp-close-ask"><span>Close this table for everyone? Everyone here is sent back to their list of tables.</span>
          <span class="mp-row-btns"><button class="ui-btn-ghost" data-do="close-no" type="button">Cancel</button>
          <button class="ui-btn mp-danger" data-do="close-yes" type="button">Yes, close it</button></span></div>`
      : `<p class="mp-mute mp-exit"><button class="ui-link" data-do="close" type="button">Close this table</button> ends it for everyone — use this if you won't be playing at it.</p>`) : ""}
    <p class="mp-mute mp-exit">${t.status === "lobby"
      ? `<button class="ui-link" data-do="leave" type="button">Leave this table</button> gives up your seat. <b>‹ All my tables</b> keeps it — come back any time.`
      : `<b>‹ All my tables</b> takes you out of this screen; your seat stays yours and you can rejoin from the list.`}</p>`;
  // the colour wheel (any colour): one wheel kept across redraws, so it stays open while the table changes
  const box = lobby.querySelector<HTMLElement>(".mp-swatches");
  if (box && mine?.kind === "human") {
    const w = lobbyWheel();
    box.appendChild(w.button);
    box.after(w.pop);
    w.button.disabled = !me.controlling;
    w.mark(PALETTE.some((p) => p.colour === mine.colour) ? null : mine.colour);
  }
}

let wheel: ColourWheel | null = null, wheelTimer = 0;
/** Colours other people at this table have (the wheel refuses one too close to any of them). */
function othersColours(): NamedColour[] {
  if (!table || !you) return [];
  return table.seats.flatMap((s, i) => (s?.kind === "human" && i !== you!.seat ? [{ colour: s.colour, name: s.name }] : []));
}
function lobbyWheel(): ColourWheel {
  // it applies as you drag: send the colour once you pause, not on every movement
  wheel ??= colourWheel({ swatchClass: "mp-sw", taken: othersColours, onPick: (c) => {
    clearTimeout(wheelTimer);
    wheelTimer = window.setTimeout(() => send({ type: "setColour", colour: c } as never), 350);
  } });
  return wheel;
}

/** Drawn avatars (a player without a photo): main.ts provides the drawing. */
let drawAvatar: (cfgJson: string, colour: string) => string = () => "";
export function setAvatarDrawer(fn: (cfgJson: string, colour: string) => string): void { drawAvatar = fn; }
/** The people at the current table, by player id: their photo or drawn avatar (for the game board). */
export function tableLooks(): Map<string, { avatarUrl: string | null; avatarCfg: string | null }> {
  const out = new Map<string, { avatarUrl: string | null; avatarCfg: string | null }>();
  for (const s of table?.seats ?? []) if (s?.kind === "human") out.set(s.playerId, { avatarUrl: s.avatarUrl, avatarCfg: s.avatarCfg ?? null });
  return out;
}
